// src/pages/DocumentsPage.jsx
import { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  useMediaQuery,
  useTheme,
  Fade,
  alpha,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Button,
  Grid,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Dialog,
  DialogContent,
  IconButton as MuiIconButton,
  LinearProgress,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
} from "@mui/material";
import {
  Image,
  CloudUpload,
  Close,
  ZoomIn,
  AddPhotoAlternate,
  Delete,
  Check,
  Error,
  Refresh,
  Login,
  Logout,
  Google,
  PhotoLibrary,
  FolderOpen,
  InsertDriveFile,
  Public,
  VpnKey,
} from "@mui/icons-material";
import { format } from "date-fns";
import { id } from "date-fns/locale";
import {
  getFilesFromFolder,
  uploadToGoogleDrive,
  deleteFileFromDrive,
} from "../config/googleDriveService";
import { useAuth } from "../config/authContext";

export default function DocumentsPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const fileInputRef = useRef(null);

  // Auth
  const { user, login, logout, isAuthenticated, accessToken } = useAuth();

  const [evidentPhotos, setEvidentPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);

  // Upload states
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadStatus, setUploadStatus] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showUploadSection, setShowUploadSection] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [photoSource, setPhotoSource] = useState("");

  // Load photos from Google Drive
  const loadPhotos = async () => {
    setLoading(true);
    setError("");

    try {
      const photos = await getFilesFromFolder();
      setEvidentPhotos(photos);

      // Cek sumber foto
      if (photos.length > 0 && photos[0]?.source) {
        setPhotoSource(photos[0].source);
      } else if (photos.length > 0) {
        setPhotoSource("Google Drive");
      } else {
        setPhotoSource("");
      }

      setLoading(false);
    } catch (err) {
      console.error("Error loading photos:", err);
      setError("Gagal memuat foto evident. Silakan coba lagi.");
      setLoading(false);

      if (err.message === "SESSION_EXPIRED") {
        logout();
      }
    }
  };

  // Load photos on mount and when auth changes
  useEffect(() => {
    loadPhotos();
  }, [isAuthenticated, accessToken]);

  // Handle file selection
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    setSelectedFiles(files);
    setUploadStatus([]);
    setError("");
  };

  // Handle file upload
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setSnackbarMessage("Silakan pilih file terlebih dahulu");
      setSnackbarSeverity("warning");
      setSnackbarOpen(true);
      return;
    }

    if (!isAuthenticated) {
      setSnackbarMessage("Silakan login terlebih dahulu untuk upload");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError("");

    const statuses = [];
    let completed = 0;

    for (const file of selectedFiles) {
      try {
        const fileName = `${Date.now()}_${file.name}`;
        const result = await uploadToGoogleDrive(file, fileName);
        statuses.push({
          fileName: file.name,
          status: "success",
          message: "Berhasil diupload",
          link: result.viewLink,
          data: result,
        });
      } catch (err) {
        let errorMessage = err.message || "Gagal upload";
        if (err.message === "SESSION_EXPIRED") {
          errorMessage = "Sesi expired. Silakan login ulang.";
          logout();
        }
        statuses.push({
          fileName: file.name,
          status: "error",
          message: errorMessage,
        });
      }

      completed++;
      setUploadProgress(Math.round((completed / selectedFiles.length) * 100));
    }

    setUploadStatus(statuses);
    setUploading(false);
    setSelectedFiles([]);

    // Refresh photos
    await loadPhotos();

    const successCount = statuses.filter((s) => s.status === "success").length;
    const errorCount = statuses.filter((s) => s.status === "error").length;

    if (errorCount === 0) {
      setSnackbarMessage(`${successCount} file berhasil diupload`);
      setSnackbarSeverity("success");
    } else if (successCount > 0) {
      setSnackbarMessage(`${successCount} berhasil, ${errorCount} gagal`);
      setSnackbarSeverity("warning");
    } else {
      setSnackbarMessage("Upload gagal semua file");
      setSnackbarSeverity("error");
    }
    setSnackbarOpen(true);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClearFiles = () => {
    setSelectedFiles([]);
    setUploadStatus([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeletePhoto = async (photoId, photoName) => {
    if (!window.confirm(`Hapus foto "${photoName}" dari Google Drive?`)) {
      return;
    }

    if (!isAuthenticated) {
      setSnackbarMessage("Silakan login terlebih dahulu");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setDeleting(true);
    try {
      await deleteFileFromDrive(photoId);

      // Refresh photos
      await loadPhotos();

      setSnackbarMessage(`✅ Foto "${photoName}" berhasil dihapus`);
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } catch (err) {
      let errorMessage = err.message || "Gagal hapus foto";
      if (err.message === "SESSION_EXPIRED") {
        errorMessage = "Sesi expired. Silakan login ulang.";
        logout();
      }
      setSnackbarMessage(`❌ ${errorMessage}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setDeleting(false);
    }
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setImageDialogOpen(true);
  };

  const handleCloseImageDialog = () => {
    setImageDialogOpen(false);
    setSelectedImage(null);
  };

  const handleRefresh = () => {
    loadPhotos();
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
  };

  // Loading State
  if (loading) {
    return (
      <Fade in>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 6,
              borderRadius: 4,
              textAlign: "center",
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <CircularProgress
              size={50}
              thickness={4}
              sx={{ color: "#2e7d32" }}
            />
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
              Memuat foto evident dari Google Drive...
            </Typography>
          </Paper>
        </Container>
      </Fade>
    );
  }

  // Error State
  if (error) {
    return (
      <Fade in>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <Alert
            severity="error"
            sx={{
              borderRadius: 3,
              "& .MuiAlert-icon": { fontSize: 24 },
            }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh}>
                Coba Lagi
              </Button>
            }
          >
            {error}
          </Alert>
        </Container>
      </Fade>
    );
  }

  return (
    <Fade in timeout={500}>
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3, md: 4 },
            borderRadius: 4,
            bgcolor: "background.paper",
            border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
            boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          {/* HEADER */}
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={isMobile ? 2 : 0}
            sx={{ mb: 4 }}
          >
            <Box>
              <Typography
                variant={isMobile ? "h5" : "h4"}
                sx={{
                  fontWeight: 800,
                  background: `linear-gradient(135deg, #1b5e20 0%, #2e7d32 40%, #4caf50 100%)`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                📸 Foto Evident
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {evidentPhotos.length} foto evident
                {isAuthenticated && user && ` • 👤 ${user.name || user.email}`}
                {photoSource && ` • 📡 ${photoSource}`}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
              {!isAuthenticated ? (
                <Button
                  variant="contained"
                  startIcon={<Google />}
                  onClick={login}
                  sx={{
                    borderRadius: 3,
                    bgcolor: "#4285f4",
                    "&:hover": { bgcolor: "#357abd" },
                    textTransform: "none",
                    px: 3,
                  }}
                >
                  🔑 Login untuk Upload
                </Button>
              ) : (
                <>
                  <Tooltip title="Upload Foto Evident" arrow>
                    <Button
                      variant="contained"
                      startIcon={<AddPhotoAlternate />}
                      onClick={() => setShowUploadSection(!showUploadSection)}
                      sx={{
                        borderRadius: 3,
                        bgcolor: "#2e7d32",
                        "&:hover": { bgcolor: "#1b5e20" },
                        textTransform: "none",
                      }}
                    >
                      {isMobile ? "Upload" : "Tambah Evident"}
                    </Button>
                  </Tooltip>
                  <Tooltip title="Logout Google Drive" arrow>
                    <IconButton
                      onClick={logout}
                      sx={{
                        color: "#d32f2f",
                        bgcolor: alpha("#d32f2f", 0.08),
                        "&:hover": {
                          bgcolor: alpha("#d32f2f", 0.15),
                        },
                      }}
                    >
                      <Logout />
                    </IconButton>
                  </Tooltip>
                </>
              )}
              <Tooltip title="Refresh" arrow>
                <IconButton
                  onClick={handleRefresh}
                  disabled={loading}
                  sx={{
                    color: "#2e7d32",
                    bgcolor: alpha("#2e7d32", 0.08),
                    "&:hover": {
                      bgcolor: alpha("#2e7d32", 0.15),
                    },
                  }}
                >
                  <Refresh />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* INFO ALERT - Belum Login */}
          {!isAuthenticated && (
            <Alert
              severity="info"
              icon={<Login />}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
              action={
                <Button
                  color="info"
                  size="small"
                  onClick={login}
                  startIcon={<Google />}
                  sx={{ textTransform: "none" }}
                >
                  Login Sekarang
                </Button>
              }
            >
              <Typography variant="body2">
                <strong>💡 Login dengan Google Drive</strong> untuk upload,
                hapus, dan kelola foto evident.
                {evidentPhotos.length > 0 &&
                  ` Saat ini ${evidentPhotos.length} foto dapat dilihat (read-only).`}
              </Typography>
            </Alert>
          )}

          {/* STATUS - Sudah Login tapi tidak ada foto */}
          {isAuthenticated && evidentPhotos.length === 0 && !loading && (
            <Alert
              severity="success"
              icon={<Check />}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
              }}
            >
              <Typography variant="body2">
                ✅ Berhasil terhubung dengan Google Drive. Belum ada foto
                evident.
                <Button
                  size="small"
                  onClick={() => setShowUploadSection(true)}
                  sx={{ ml: 1, textTransform: "none" }}
                >
                  Upload foto pertama Anda!
                </Button>
              </Typography>
            </Alert>
          )}

          {/* STATUS - Ada foto dengan API Key (read-only) */}
          {evidentPhotos.length > 0 && !isAuthenticated && (
            <Alert
              severity="info"
              icon={<Public />}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
              }}
            >
              <Typography variant="body2">
                📸 Menampilkan {evidentPhotos.length} foto evident (read-only).
                Login untuk upload dan delete foto.
              </Typography>
            </Alert>
          )}

          {/* Upload Section - Toggle */}
          {showUploadSection && isAuthenticated && (
            <Card
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 3,
                border: `2px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                bgcolor: alpha(theme.palette.background.default, 0.5),
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack spacing={2}>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    justifyContent="space-between"
                    alignItems={isMobile ? "flex-start" : "center"}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      <CloudUpload sx={{ verticalAlign: "middle", mr: 1 }} />
                      Upload Foto Evident
                      {user && (
                        <Chip
                          label={user.name || user.email}
                          size="small"
                          sx={{ ml: 1, fontSize: "0.7rem" }}
                        />
                      )}
                    </Typography>
                    <Button
                      size="small"
                      onClick={() => {
                        setShowUploadSection(false);
                        setSelectedFiles([]);
                        setUploadStatus([]);
                      }}
                      sx={{ color: "text.secondary" }}
                    >
                      Tutup
                    </Button>
                  </Stack>

                  {/* File Input */}
                  <Box>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      id="evident-upload"
                    />
                    <label htmlFor="evident-upload">
                      <Button
                        component="span"
                        variant="outlined"
                        startIcon={<AddPhotoAlternate />}
                        fullWidth
                        sx={{
                          py: 2,
                          borderRadius: 3,
                          borderStyle: "dashed",
                          textTransform: "none",
                        }}
                      >
                        {selectedFiles.length > 0
                          ? `${selectedFiles.length} file dipilih`
                          : "Pilih Foto (multiple)"}
                      </Button>
                    </label>
                  </Box>

                  {/* Selected Files List */}
                  {selectedFiles.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        File terpilih:
                      </Typography>
                      <List
                        dense
                        sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                      >
                        {selectedFiles.map((file, index) => (
                          <ListItem
                            key={index}
                            divider={index < selectedFiles.length - 1}
                          >
                            <ListItemIcon>
                              <Image sx={{ color: "#2e7d32" }} />
                            </ListItemIcon>
                            <ListItemText
                              primary={file.name}
                              secondary={`${(file.size / 1024).toFixed(1)} KB`}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Upload Progress */}
                  {uploading && (
                    <Box>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress}
                        sx={{ height: 8, borderRadius: 4 }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                      >
                        Uploading... {uploadProgress}%
                      </Typography>
                    </Box>
                  )}

                  {/* Upload Status */}
                  {uploadStatus.length > 0 && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Status Upload:
                      </Typography>
                      <List
                        dense
                        sx={{ bgcolor: "background.paper", borderRadius: 2 }}
                      >
                        {uploadStatus.map((status, index) => (
                          <ListItem
                            key={index}
                            divider={index < uploadStatus.length - 1}
                          >
                            <ListItemIcon>
                              {status.status === "success" ? (
                                <Check sx={{ color: "success.main" }} />
                              ) : (
                                <Error sx={{ color: "error.main" }} />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={status.fileName}
                              secondary={status.message}
                              secondaryTypographyProps={{
                                color:
                                  status.status === "success"
                                    ? "success.main"
                                    : "error.main",
                              }}
                            />
                            {status.link && (
                              <Button
                                size="small"
                                href={status.link}
                                target="_blank"
                                sx={{ textTransform: "none" }}
                              >
                                Lihat
                              </Button>
                            )}
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Action Buttons */}
                  <Stack direction={isMobile ? "column" : "row"} spacing={2}>
                    <Button
                      variant="contained"
                      onClick={handleUpload}
                      disabled={uploading || selectedFiles.length === 0}
                      startIcon={<CloudUpload />}
                      fullWidth
                      sx={{
                        borderRadius: 3,
                        py: 1.5,
                        bgcolor: "#2e7d32",
                        "&:hover": { bgcolor: "#1b5e20" },
                        "&:disabled": { bgcolor: alpha("#2e7d32", 0.5) },
                        textTransform: "none",
                      }}
                    >
                      {uploading ? "Uploading..." : "Upload ke Google Drive"}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleClearFiles}
                      disabled={uploading}
                      startIcon={<Delete />}
                      fullWidth
                      sx={{
                        borderRadius: 3,
                        py: 1.5,
                        borderColor: "error.main",
                        color: "error.main",
                        "&:hover": {
                          borderColor: "error.dark",
                          bgcolor: alpha(theme.palette.error.main, 0.05),
                        },
                        textTransform: "none",
                      }}
                    >
                      Hapus Semua
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* PHOTO GRID */}
          {evidentPhotos.length === 0 && !loading ? (
            <Box
              sx={{
                textAlign: "center",
                py: 8,
                px: 2,
                border: `2px dashed ${alpha(theme.palette.divider, 0.5)}`,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.background.default, 0.3),
              }}
            >
              <PhotoLibrary
                sx={{ fontSize: 60, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Belum ada foto evident
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isAuthenticated
                  ? "Upload foto evident untuk mendokumentasikan kegiatan"
                  : "Login dengan Google Drive untuk mulai upload foto"}
              </Typography>
              {!isAuthenticated && (
                <Button
                  variant="contained"
                  startIcon={<Google />}
                  onClick={login}
                  sx={{ mt: 2, borderRadius: 3, bgcolor: "#4285f4" }}
                >
                  Login dengan Google
                </Button>
              )}
            </Box>
          ) : (
            <Box>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Image sx={{ color: "#2e7d32", fontSize: 20 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Galeri Foto Evident
                </Typography>
                <Chip
                  label={`${evidentPhotos.length} foto`}
                  size="small"
                  sx={{
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    color: theme.palette.primary.main,
                    fontWeight: 500,
                    borderRadius: 2,
                  }}
                />
                {isAuthenticated && (
                  <Chip
                    icon={<Check sx={{ fontSize: 14 }} />}
                    label="Connected"
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.success.main, 0.12),
                      color: theme.palette.success.main,
                      fontWeight: 500,
                      borderRadius: 2,
                    }}
                  />
                )}
              </Stack>

              <ImageList
                cols={isMobile ? 2 : 4}
                gap={12}
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                {evidentPhotos.map((photo) => (
                  <ImageListItem
                    key={photo.id}
                    sx={{
                      cursor: "pointer",
                      borderRadius: 2,
                      overflow: "hidden",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "scale(1.02)",
                        boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.12)}`,
                      },
                    }}
                    onClick={() => handleImageClick(photo)}
                  >
                    <img
                      src={`https://drive.google.com/thumbnail?id=${photo.id}&sz=w400`}
                      alt={photo.name}
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: 180,
                        objectFit: "cover",
                      }}
                      onError={(e) => {
                        e.target.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='180'%3E%3Crect width='400' height='180' fill='%23f5f5f5'/%3E%3Ctext x='200' y='90' font-family='Arial' font-size='14' fill='%23999' text-anchor='middle'%3ENo Image%3C/text%3E%3C/svg%3E";
                      }}
                    />
                    <ImageListItemBar
                      title={photo.name || "Evident Photo"}
                      subtitle={`${format(new Date(photo.createdTime), "PP", { locale: id })} • ${formatFileSize(photo.size)}`}
                      position="bottom"
                      sx={{
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)",
                      }}
                      actionIcon={
                        <Stack direction="row" spacing={0.5}>
                          <MuiIconButton
                            sx={{ color: "white" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleImageClick(photo);
                            }}
                          >
                            <ZoomIn />
                          </MuiIconButton>
                          {isAuthenticated && (
                            <MuiIconButton
                              sx={{ color: "white" }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeletePhoto(photo.id, photo.name);
                              }}
                              disabled={deleting}
                            >
                              <Delete />
                            </MuiIconButton>
                          )}
                        </Stack>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>

              {evidentPhotos.length === 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ textAlign: "center", py: 4 }}
                >
                  Belum ada foto evident. Upload foto pertama Anda!
                </Typography>
              )}
            </Box>
          )}
        </Paper>

        {/* Image Preview Dialog */}
        <Dialog
          open={imageDialogOpen}
          onClose={handleCloseImageDialog}
          maxWidth="lg"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: "transparent",
              boxShadow: "none",
            },
          }}
        >
          <DialogContent sx={{ p: 0, position: "relative" }}>
            <MuiIconButton
              onClick={handleCloseImageDialog}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "white",
                bgcolor: alpha(theme.palette.common.black, 0.5),
                "&:hover": {
                  bgcolor: alpha(theme.palette.common.black, 0.7),
                },
                zIndex: 1,
              }}
            >
              <Close />
            </MuiIconButton>
            {selectedImage && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: "80vh",
                  bgcolor: alpha(theme.palette.common.black, 0.9),
                  borderRadius: 3,
                  p: 2,
                }}
              >
                <img
                  src={`https://drive.google.com/thumbnail?id=${selectedImage.id}&sz=w800`}
                  alt={selectedImage.name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "80vh",
                    objectFit: "contain",
                    borderRadius: "8px",
                    boxShadow: `0 8px 40px ${alpha(theme.palette.common.black, 0.3)}`,
                  }}
                  onError={(e) => {
                    e.target.src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='600'%3E%3Crect width='800' height='600' fill='%23333'/%3E%3Ctext x='400' y='300' font-family='Arial' font-size='24' fill='%23999' text-anchor='middle'%3EImage not available%3C/text%3E%3C/svg%3E";
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "white",
                    mt: 2,
                    opacity: 0.7,
                  }}
                >
                  {selectedImage.name} •{" "}
                  {format(new Date(selectedImage.createdTime), "PPpp", {
                    locale: id,
                  })}
                  {selectedImage.size &&
                    ` • ${formatFileSize(selectedImage.size)}`}
                </Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<FolderOpen />}
                    href={selectedImage.webViewLink}
                    target="_blank"
                    sx={{
                      borderRadius: 3,
                      bgcolor: "#2e7d32",
                      "&:hover": { bgcolor: "#1b5e20" },
                      textTransform: "none",
                    }}
                  >
                    Buka di Google Drive
                  </Button>
                  {isAuthenticated && (
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<Delete />}
                      onClick={() => {
                        handleDeletePhoto(selectedImage.id, selectedImage.name);
                        handleCloseImageDialog();
                      }}
                      disabled={deleting}
                      sx={{
                        borderRadius: 3,
                        textTransform: "none",
                      }}
                    >
                      Hapus Foto
                    </Button>
                  )}
                </Stack>
              </Box>
            )}
          </DialogContent>
        </Dialog>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            severity={snackbarSeverity}
            sx={{
              borderRadius: 3,
              boxShadow: `0 4px 24px ${alpha(theme.palette.common.black, 0.12)}`,
            }}
            action={
              snackbarSeverity === "error" ? (
                <Button color="inherit" size="small" onClick={handleRefresh}>
                  Coba Lagi
                </Button>
              ) : undefined
            }
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Fade>
  );
}
